import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, CardFooter } from "../../components/card/card";
import CONFIG from "../../config/config";
import { useNavigate } from "react-router-dom";
import { getStatusBadgeColor } from "../../misc.js";
import genres from "../../config/genres.js";

// Format the genres by removing the first part before the colon.
const formattedGenres = genres.map((g) => g.split(":")[1].trim());

// Helper function to fetch song reels from the API.
const fetchSongReels = async (page, limit, filters = {}) => {
  const token = localStorage.getItem("token");
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (filters.title) {
    queryParams.append("title", filters.title);
  }
  if (filters.genre) {
    queryParams.append("genre", filters.genre);
  }
  if (filters.status) {
    queryParams.append("status", filters.status);
  }

  const response = await fetch(
    `${CONFIG.API_URL}/song_reel/list?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.json();
};

const SongReelList = () => {
  const [songReels, setSongReels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [selectedSongReel, setSelectedSongReel] = useState(null);
  const [youtubeId, setYoutubeId] = useState("");

  // Duplicate modal states.
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedDuplicateSongReel, setSelectedDuplicateSongReel] = useState(null);
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [duplicateChannel, setDuplicateChannel] = useState("");
  const [channels, setChannels] = useState([]);
  const [duplicateError, setDuplicateError] = useState(null);

  const navigate = useNavigate();

  // Load song reels whenever page, limit, or filters change.
  const loadSongReels = async () => {
    try {
      setIsLoading(true);
      const data = await fetchSongReels(page, limit);
      if (data.success) {
        setSongReels(data.songReels);
        setTotalPages(data.totalPages);
      } else {
        setError("Failed to load song reels");
      }
    } catch (error) {
      console.error("Error fetching song reels:", error);
      setError("Failed to load song reels. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSongReels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // Navigate to the edit screen passing the document id.
  const handleEditSongReel = (id) => {
    navigate(`/create-song-reel/${id}`);
  };

  // Delete handler (only for admins).
  const handleDeleteSongReel = async (id) => {
    if (window.confirm("Are you sure you want to delete this song reel?")) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${CONFIG.API_URL}/song_reel/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Delete failed");
        await loadSongReels();
      } catch (error) {
        console.error("Delete error:", error);
        setError("Failed to delete song reel. Please try again.");
      }
    }
  };

  // Opens the modal to mark a song reel as posted.
  const handleMarkAsPosted = (songReel) => {
    setSelectedSongReel(songReel);
    setYoutubeId("");
    setShowMarkModal(true);
  };

  // Confirm marking the song reel as posted.
  const handleConfirmMarkAsPosted = async () => {
    if (!selectedSongReel) return;
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${CONFIG.API_URL}/v1/song_reel/${selectedSongReel._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            youtubeId: youtubeId,
            status: "posted",
          }),
        }
      );
      if (!response.ok) throw new Error("Update failed");
      await loadSongReels();
      setShowMarkModal(false);
    } catch (error) {
      console.error("Error updating song reel:", error);
      setError("Failed to update song reel. Please try again.");
    }
  };

  // Download the output video.
  const handleDownloadOutput = (songReel) => {
    if (songReel.output) {
      window.open(`https://api.moatoverflow.com/videos/${songReel.output}`, "_blank");
    }
  };

  // Download the thumbnail image.
  const handleDownloadThumbnail = (songReel) => {
    if (songReel.thumbnail) {
      window.open(`https://api.moatoverflow.com/images/${songReel.thumbnail}`, "_blank");
    }
  };

  // Handle duplicate action by opening the duplicate modal.
  const handleDuplicateSongReel = async (songReel) => {
    setSelectedDuplicateSongReel(songReel);
    setDuplicateTitle(songReel.title || "");
    setDuplicateChannel("");
    setDuplicateError(null);
    // Fetch the list of channels.
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${CONFIG.API_URL}/channel`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setChannels(data.data);
      } else {
        setChannels([]);
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
      setChannels([]);
    }
    setShowDuplicateModal(true);
  };

  // Confirm duplicate: call the duplicate endpoint.
  const handleConfirmDuplicate = async () => {
    if (!selectedDuplicateSongReel) return;
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${CONFIG.API_URL}/song_reel/duplicate/${selectedDuplicateSongReel._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: duplicateTitle,
            channelId: duplicateChannel
          }),
        }
      );
      if (!response.ok) throw new Error("Duplicate failed");
      await loadSongReels();
      setShowDuplicateModal(false);
    } catch (error) {
      console.error("Error duplicating song reel:", error);
      setDuplicateError("Failed to duplicate song reel. Please try again.");
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  if (isLoading) return <div className="text-center mt-5">Loading...</div>;
  if (error) return <div className="alert alert-danger mt-5">{error}</div>;

  return (
    <div>
      <Card>
        <CardHeader>
          <h1>Song Reel List</h1>
        </CardHeader>
        <CardBody>
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Position</th>
                <th>Title</th>
                <th>Genre</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Video ID</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {songReels.map((songReel, index) => (
                <tr key={songReel._id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>{songReel.title || "N/A"}</td>
                  <td>{songReel.genre || "N/A"}</td>
                  <td>{songReel.channel ? songReel.channel.name : "N/A"}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeColor(songReel.status)}`}>
                      {songReel.status}
                    </span>
                  </td>
                  <td>{songReel.video_id || "N/A"}</td>
                  <td>{new Date(songReel.createdAt).toLocaleString()}</td>
                  <td>
                    {/* Edit Action */}
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleEditSongReel(songReel._id)}
                      title="Edit"
                    >
                      <i className="fas fa-pencil-alt" />
                    </button>
                    {/* Delete Action (for admins) */}
                    {localStorage.getItem("role") === "admin" && (
                      <button
                        className="btn btn-link p-0 ms-2 text-danger"
                        onClick={() => handleDeleteSongReel(songReel._id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    )}
                    {/* Duplicate Action */}
                    {["song_generated", "generating_song", "pending", "processing", "processed", "posted"].includes(
                      songReel.status
                    ) && (
                      <button
                        className="btn btn-link p-0 ms-2"
                        onClick={() => handleDuplicateSongReel(songReel)}
                        title="Duplicate"
                      >
                        <i className="fas fa-copy" />
                      </button>
                    )}
                    {/* Actions for processed song reels */}
                    {songReel.status === "processed" && (
                      <>
                        <button
                          className="btn btn-link p-0 ms-2 text-primary"
                          onClick={() => handleMarkAsPosted(songReel)}
                          title="Mark as Posted"
                        >
                          <i className="fas fa-upload" />
                        </button>
                        <button
                          className="btn btn-link p-0 ms-2"
                          onClick={() => handleDownloadOutput(songReel)}
                          title="Download Output"
                        >
                          <i className="fas fa-download" />
                        </button>
                        <button
                          className="btn btn-link p-0 ms-2"
                          onClick={() => handleDownloadThumbnail(songReel)}
                          title="Download Thumbnail"
                        >
                          <i className="fas fa-image" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
        <CardFooter>
          <div className="d-flex justify-content-between align-items-center">
            <button
              className="btn btn-warning"
              onClick={handlePreviousPage}
              disabled={page === 1}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-success"
              onClick={handleNextPage}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </CardFooter>
      </Card>

      {/* Mark as Posted Modal */}
      {showMarkModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Mark as Posted</h5>
                <button type="button" className="btn-close" onClick={() => setShowMarkModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to mark this song reel as posted? Please enter the YouTube ID of the published video.
                </p>
                <input
                  type="text"
                  className="form-control"
                  value={youtubeId}
                  onChange={(e) => setYoutubeId(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowMarkModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleConfirmMarkAsPosted}
                  disabled={!youtubeId}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Song Reel Modal */}
      {showDuplicateModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Duplicate Song Reel</h5>
                <button type="button" className="btn-close" onClick={() => setShowDuplicateModal(false)}></button>
              </div>
              <div className="modal-body">
                {duplicateError && <div className="alert alert-danger">{duplicateError}</div>}
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={duplicateTitle}
                    onChange={(e) => setDuplicateTitle(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Target Channel</label>
                  <select
                    className="form-select"
                    value={duplicateChannel}
                    onChange={(e) => setDuplicateChannel(e.target.value)}
                  >
                    <option value="">Select a channel</option>
                    {channels.map((channel) => (
                      <option key={channel._id} value={channel._id}>
                        {channel.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDuplicateModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleConfirmDuplicate}
                  disabled={!duplicateTitle || !duplicateChannel}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongReelList;
